import React, { useRef, useState } from 'react';
import { Card, Typography, Space, Button, Divider, Alert } from 'antd';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import manualMd from '../../docs/ProductManual.md?raw';

const { Title, Text } = Typography;

// 极简 Markdown -> HTML 渲染（覆盖标题、列表、段落与内联链接）
function renderMarkdown(md) {
  const lines = md.split('\n');
  const elements = [];
  let listBuffer = [];

  const slugify = (text) => {
    const s = String(text || '').trim();
    return s
      .replace(/\./g, '')
      .replace(/\s+/g, '-')
      .replace(/[^\w\-\u4e00-\u9fa5]/g, '');
  };

  const parseInline = (s) => {
    const parts = [];
    const str = String(s || '');
    const regex = /\[(.+?)\]\((.+?)\)/g;
    let lastIndex = 0; let m;
    while ((m = regex.exec(str)) !== null) {
      const before = str.slice(lastIndex, m.index);
      if (before) parts.push(before);
      parts.push(<a href={m[2]} key={`a-${parts.length}`}>{m[1]}</a>);
      lastIndex = regex.lastIndex;
    }
    const tail = str.slice(lastIndex);
    if (tail) parts.push(tail);
    return parts.length ? parts : [str];
  };

  const flushList = () => {
    if (listBuffer.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} style={{ marginTop: 8, marginBottom: 8 }}>
          {listBuffer.map((item, idx) => (
            <li key={`li-${elements.length}-${idx}`}>{parseInline(item)}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (!line) {
      flushList();
      elements.push(<div key={`br-${i}`} style={{ height: 8 }} />);
      return;
    }
    if (line.startsWith('### ')) {
      flushList();
      const txt = line.replace(/^###\s+/, '');
      elements.push(<Title level={4} id={slugify(txt)} key={`h3-${i}`}>{txt}</Title>);
      return;
    }
    if (line.startsWith('## ')) {
      flushList();
      const txt = line.replace(/^##\s+/, '');
      elements.push(<Title level={3} id={slugify(txt)} key={`h2-${i}`}>{txt}</Title>);
      return;
    }
    if (line.startsWith('# ')) {
      flushList();
      const txt = line.replace(/^#\s+/, '');
      elements.push(<Title level={2} id={slugify(txt)} key={`h1-${i}`}>{txt}</Title>);
      return;
    }
    if (line.startsWith('- ')) {
      listBuffer.push(line.replace(/^-\s+/, ''));
      return;
    }
    flushList();
    elements.push(<Text key={`p-${i}`}>{parseInline(line)}</Text>);
  });

  flushList();
  return elements;
}

export default function ManualPage() {
  const containerRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const node = containerRef.current;
      if (!node) return;
      const canvas = await html2canvas(node, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = canvas.height * (imgWidth / canvas.width);
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('产品说明书.pdf');
    } catch (error) {
      console.error('导出 PDF 失败:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ maxWidth: 960, margin: '24px auto' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={2}><FileTextOutlined /> 产品说明书</Title>
          <Text type="secondary">版本：预览版（以 docs/ProductManual.md 为准）</Text>
          <Alert type="info" showIcon message="提示：点击右侧按钮可以导出当前页面为 PDF。" />
          <div style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExportPdf}>
              导出 PDF
            </Button>
          </div>
          <Divider />
          <div ref={containerRef} style={{ background: '#fff', padding: 16 }}>
            {renderMarkdown(manualMd)}
          </div>
        </Space>
      </Card>
    </div>
  );
}